
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Sun, Moon, MapPin } from 'lucide-react';
import { VisibilityBars } from './VisibilityBars';
import { getRowColorClasses } from './rowColors';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useIsMobile } from '@/hooks/use-mobile';
import { getMoonPhaseEmoji } from '@/utils/moonCalculations';

export { getRowColorClasses };

interface DayData {
  date: number;
  fullDate: string;
  sunrise: string;
  sunset: string;
  moonrise: string;
  moonset: string;
  astroTwilightMorning: string;
  astroTwilightEvening: string;
  galacticCoreVisible: boolean;
  galacticCoreStart: string;
  galacticCoreEnd: string;
  moonPhase: number;
  moonPhaseCycle?: number;
  notVisibleReason?: string;
  maxElevation?: number;
}

interface CalendarTableProps {
  monthData: DayData[];
  selectedDay?: number;
  onDaySelect?: (dayDate: number) => void;
}

export const CalendarTable: React.FC<CalendarTableProps> = ({ monthData, selectedDay, onDaySelect }) => {
  const isMobile = useIsMobile();

  // Helper function to get visibility rating (0-10)
  const getVisibilityRating = (day: DayData) => {
    const moonPercent = Math.round(day.moonPhase * 100);
    
    // If galactic core is not visible, rating is 0
    if (!day.galacticCoreVisible || 
        day.galacticCoreStart === 'Not visible' || 
        day.galacticCoreStart === 'No twilight' ||
        day.galacticCoreStart === 'Below horizon' ||
        day.galacticCoreStart === '--:--' ||
        day.galacticCoreStart === 'Not dark enough' ||
        day.galacticCoreStart === 'Too brief') {
      return 0;
    }
    
    // Rating based on moon phase
    if (moonPercent <= 5) return 10;      // New moon - perfect
    else if (moonPercent <= 10) return 9; // Excellent
    else if (moonPercent <= 15) return 8; // Very good
    else if (moonPercent <= 20) return 7; // Good
    else if (moonPercent <= 25) return 6; // Decent
    else if (moonPercent <= 35) return 5; // Fair
    else if (moonPercent <= 45) return 4; // Challenging
    else if (moonPercent <= 55) return 3; // Difficult
    else if (moonPercent <= 70) return 2; // Very difficult
    else if (moonPercent <= 85) return 1; // Extremely difficult
    else return 0; // Not viable
  };

  if (isMobile) {
    return (
      <TooltipProvider>
        <div className="space-y-4">
          {monthData.map((day) => (
            <Card key={day.date} className={`glass-card border-border/50 shadow-md ${getRowColorClasses(getVisibilityRating(day)).border} ${getRowColorClasses(getVisibilityRating(day)).row}`}>
              <CardContent className="p-4">
                <div className="space-y-3">
                  {/* Date and Visibility */}
                  <div className="flex justify-between items-center">
                    <h3 className="font-semibold text-lg">{day.fullDate}</h3>
                    <div className="flex items-center gap-3">
                      <div className="text-center">
                        <div className="text-xs text-muted-foreground mb-1">Visibility</div>
                        <VisibilityBars rating={getVisibilityRating(day)} />
                      </div>
                      <span className="text-2xl">{getMoonPhaseEmoji(day.moonPhase, day.moonPhaseCycle)}</span>
                    </div>
                  </div>
                  
                  {/* Sun Times */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 rounded-lg">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                        <Sun className="w-4 h-4" />
                        <span className="text-xs font-medium">Sunrise</span>
                      </div>
                      <p className="text-sm font-mono">{day.sunrise}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-amber-600 mb-1">
                        <Sun className="w-4 h-4" />
                        <span className="text-xs font-medium">Sunset</span>
                      </div>
                      <p className="text-sm font-mono">{day.sunset}</p>
                    </div>
                  </div>

                  {/* Moon Times */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-muted/50 rounded-lg">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                        <Moon className="w-4 h-4" />
                        <span className="text-xs font-medium">Moonrise</span>
                      </div>
                      <p className="text-sm font-mono">{day.moonrise}</p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-galaxy-blue mb-1">
                        <Moon className="w-4 h-4" />
                        <span className="text-xs font-medium">Moonset</span>
                      </div>
                      <p className="text-sm font-mono">{day.moonset}</p>
                    </div>
                  </div>

                  {/* Milky Way Times */}
                  <div className="grid grid-cols-2 gap-3 p-3 bg-amber-50 rounded-lg">
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gold-foreground mb-1">
                        <span className="text-sm">✨</span>
                        <span className="text-xs font-medium">Galactic Core Start</span>
                      </div>
                      <p className={`text-sm font-mono ${day.galacticCoreVisible ? "text-gold-foreground font-medium" : "text-muted-foreground"}`}>
                        {day.galacticCoreStart}
                      </p>
                    </div>
                    <div className="text-center">
                      <div className="flex items-center justify-center gap-1 text-gold-foreground mb-1">
                        <span className="text-sm">✨</span>
                        <span className="text-xs font-medium">Galactic Core End</span>
                      </div>
                      <p className={`text-sm font-mono ${day.galacticCoreVisible ? "text-gold-foreground font-medium" : "text-muted-foreground"}`}>
                        {day.galacticCoreEnd}
                      </p>
                    </div>
                  </div>

                  {/* Moon Phase Percentage */}
                  <div className="text-center p-2 bg-gray-50 rounded-lg">
                    <span className="text-xs text-muted-foreground">Moon Illumination: </span>
                    <span className="text-sm font-medium">{Math.round(day.moonPhase * 100)}%</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <Card className="glass-card border-border/50 shadow-lg overflow-hidden">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50 bg-secondary/20">
                  <TableHead className="text-foreground font-medium py-4 min-w-[90px] sticky left-0 bg-secondary/20 z-10">Date</TableHead>
                  <TableHead className="text-amber-600 font-medium min-w-[70px]">
                    <div className="flex items-center">
                      <Sun className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Sunrise</span>
                      <span className="sm:hidden">Rise</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-amber-600 font-medium min-w-[70px]">
                    <div className="flex items-center">
                      <Sun className="w-3 h-3 mr-1" />
                      <span className="hidden sm:inline">Sunset</span>
                      <span className="sm:hidden">Set</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[70px] hidden md:table-cell">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1" />
                      <span className="hidden lg:inline">Moonrise</span>
                      <span className="lg:hidden">Rise</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[70px] hidden md:table-cell">
                    <div className="flex items-center">
                      <Moon className="w-3 h-3 mr-1" />
                      <span className="hidden lg:inline">Moonset</span>
                      <span className="lg:hidden">Set</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                    <div className="flex items-center">
                      <span className="mr-1">✨</span>
                      <span className="hidden xl:inline whitespace-normal">Galactic Core Start</span>
                      <span className="xl:hidden">GC Start</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-space-gray font-medium min-w-[80px] hidden lg:table-cell">
                    <div className="flex items-center">
                      <span className="mr-1">✨</span>
                      <span className="hidden xl:inline whitespace-normal">Galactic Core End</span>
                      <span className="xl:hidden">GC End</span>
                    </div>
                  </TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center hidden sm:table-cell">
                    <span className="hidden md:inline">Moon %</span>
                    <span className="md:hidden">%</span>
                  </TableHead>
                  <TableHead className="text-muted-foreground font-medium min-w-[50px] text-center">
                    <span className="hidden sm:inline">Phase</span>
                    <span className="sm:hidden">🌙</span>
                  </TableHead>
                  <TableHead className="text-galaxy-blue font-medium min-w-[80px] text-center sticky right-0 bg-secondary/20 z-10">
                    Visibility
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {monthData.map((day) => {
                  const colors = getRowColorClasses(getVisibilityRating(day));
                  const isSelected = selectedDay === day.date;
                  return (
                  <TableRow
                    key={day.date}
                    className={`border-border/30 transition-colors ${
                      isSelected
                        ? 'bg-gold/10 hover:bg-gold/15 ring-1 ring-gold/30'
                        : `hover:bg-secondary/20 ${colors.row}`
                    } ${onDaySelect ? 'cursor-pointer' : ''}`}
                    onClick={() => onDaySelect?.(isSelected ? -1 : day.date)}
                  >
                    <TableCell
                      className={`text-foreground font-medium py-3 sticky left-0 z-10 border-r border-border/30 whitespace-nowrap ${colors.sticky} ${colors.border}`}
                      onClick={(e) => {
                        if (onDaySelect) {
                          e.stopPropagation();
                          onDaySelect(isSelected ? -1 : day.date);
                        }
                      }}
                    >
                      <div className="text-sm flex items-center gap-1.5">
                        {onDaySelect && <MapPin className={`w-3 h-3 flex-shrink-0 ${isSelected ? 'text-gold' : 'text-muted-foreground/40'}`} />}
                        {day.fullDate}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{day.sunrise}</TableCell>
                    <TableCell className="text-muted-foreground text-xs whitespace-nowrap">{day.sunset}</TableCell>
                    <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{day.moonrise}</TableCell>
                    <TableCell className="text-muted-foreground text-xs hidden md:table-cell">{day.moonset}</TableCell>
                    <TableCell className={`${day.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                      {day.galacticCoreStart}
                    </TableCell>
                    <TableCell className={`${day.galacticCoreVisible ? "text-space-gray font-medium" : "text-muted-foreground"} text-xs hidden lg:table-cell`}>
                      {day.galacticCoreEnd}
                    </TableCell>
                    <TableCell className="text-muted-foreground text-center text-xs hidden sm:table-cell">{Math.round(day.moonPhase * 100)}%</TableCell>
                    <TableCell className="text-xl text-center">
                      {getMoonPhaseEmoji(day.moonPhase, day.moonPhaseCycle)}
                    </TableCell>
                    <TableCell className={`text-center sticky right-0 z-10 border-l border-border/30 ${colors.sticky}`}>
                      <VisibilityBars rating={getVisibilityRating(day)} />
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </TooltipProvider>
  );
};
